import React, { useCallback, useEffect, useState } from 'react'
import { Datagrid, ListContextProvider, NumberInput, RecordContextProvider, TextField, TextInput, useList, useNotify, useRecordContext, useTranslate } from 'react-admin'
import { useField, useForm, useFormState } from 'react-final-form'
import { Button, Card, Collapse, Grid, IconButton, InputAdornment, makeStyles, Typography } from '@material-ui/core'
import { Add, Delete, ExpandLess, ExpandMore, Visibility } from '@material-ui/icons'
import { useSelector } from 'react-redux'
import moment from 'moment-timezone'

import { sortBy, uniqueId } from '@hisports/common/src/lodash'
import { SURFACE_EVENT_TYPES } from '@hisports/common/src/constants'

import { useAssociationSchedulingContext } from '../../../../common/calendar/schedulers/association/AssociationSchedulingContext'
import { FieldDependency } from '../../../../common/FieldDependency'
import { ListView } from '../../../../common/cards/ListCard'
import TimeRangeField from '../../../../common/fields/TimeRangeField'
import { ArenaSlotTypeEnumInput, EnumInput } from '../../../../common/inputs/EnumInputs'
import { useSeason } from '../../../../common/inputs/SeasonSelector'
import SwitchInput from '../../../../common/inputs/SwitchInput'
import TimeInput from '../../../../common/inputs/TimeInput'
import useKeyboardEvent from '../../../../common/hooks/useKeyboardEvent'

import { SurfaceField } from '../../../surfaces/SurfaceField'
import { SurfaceInput } from '../../../surfaces/SurfaceInput'
import { CategoryInput } from '../../../categories/CategoryInput'
import { OfficeInput } from '../../../offices/OfficeInput'
import { TeamInput } from '../../../teams/TeamInput'
import SlotDetailsPopover from './SlotDetailsPopover'
import AutoGeneratedNameInput from './AutoGeneratedNameInput'

const useStyles = makeStyles(theme => ({
  actionButtons: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: theme.spacing(0.5),
  },
  collapseLabel: {
    display: 'flex',
    gap: theme.spacing(0.5),
    alignItems: 'flex-end',
  },
  truncate: {
    maxWidth: 175,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  scrollTable: {
    overflowX: 'auto',
    maxWidth: '100%',
    minWidth: 0,
    whiteSpace: 'nowrap',
  },
}))

const defaultTimezone = moment.tz.guess();

const inputProps = {
  resource: 'arenaslots',
  basePath: '/arenaslots',
  variant: 'outlined',
  margin: 'none',
  fullWidth: true,
}

const formatDuration = ({ duration, startTime, endTime }) => {
  const diff = duration || moment(endTime).diff(startTime, 'minutes');
  const minutes = diff % 60
  const hours = Math.floor(diff / 60)
  if (minutes <= 0) return `${hours}h`
  if (hours <= 0) return `${minutes}m`
  return `${hours}h ${minutes}m`
}

const isOverlap = (slots, newSlot) => {
  return slots.some(slot => {
    if (slot.surfaceId !== newSlot.surfaceId) return false;

    const newSlotRange = moment.range(newSlot.startTime, newSlot.endTime);
    const existingSlotRange = moment.range(slot.startTime, slot.endTime);

    return newSlotRange.overlaps(existingSlotRange);
  });
}

const DurationInput = props => {
  const translate = useTranslate();
  return <NumberInput {...props} InputProps={{
    endAdornment: <InputAdornment position="end">
      {translate('ra.input.adornments.minutes')}
    </InputAdornment>
  }} />
}

const EndTimeInput = props => {
  const { change } = useForm();
  const { values: { startTime, duration } } = useFormState();

  useEffect(() => {
    if (duration && startTime) {
      change('endTime', moment.tz(startTime, defaultTimezone).add(duration, 'minutes').toISOString());
    }
  }, [startTime, duration, change]);

  return <TimeInput {...props} disabled={!!duration} />
}

const EventTypeEnumInput = props => {
  const { change, batch } = useForm();
  const translate = useTranslate();

  const handleChange = (value) => {
    if (value !== SURFACE_EVENT_TYPES.TIMESLOT) {
      batch(() => {
        change('categoryIds', []);
        change('types', []);
        change('leagueIds', []);
      });
    } else if (value !== SURFACE_EVENT_TYPES.PRACTICE) {
      batch(() => {
        change('teamIds', []);
      });
    }
  }

  return <EnumInput {...props} select onChange={handleChange} choices={[
    { id: SURFACE_EVENT_TYPES.TIMESLOT, name: translate('resources.arenaslots.name', 1) },
    { id: SURFACE_EVENT_TYPES.PRACTICE, name: translate('resources.practices.name', 1) },
  ]} />
}

const RemoveButton = ({ slots }) => {
  const record = useRecordContext();
  const { change } = useForm();

  const handleRemove = () => {
    change('slots', slots.filter(slot => slot.id !== record.id));
  }

  return <IconButton size="small" onClick={handleRemove}>
    <Delete fontSize="small" />
  </IconButton>
}

const ShowButton = () => {
  const record = useRecordContext();

  return <RecordContextProvider value={record}>
    <SlotDetailsPopover>
      {(onClick) => {
        return <IconButton size="small" onClick={onClick}>
          <Visibility color="primary" fontSize="small" />
        </IconButton>
      }}
    </SlotDetailsPopover>
  </RecordContextProvider>
}

const SlotsGrid = () => {
  const { input: { value: slots } } = useField('slots');
  const translate = useTranslate();
  const classes = useStyles();

  const context = useList({
    resource: 'arenaslots',
    data: slots || [],
    loading: false,
    loaded: true,
    page: 1,
    perPage: 15,
  });

  return <Grid container spacing={2}>
    <Grid item xs={12}>
      <Typography align="center" variant="h6" color="textSecondary" style={{ fontWeight: 500, marginTop: 16 }}>
        {translate('resources.arenaslots.labels.slots')}
      </Typography>
    </Grid>
    <Grid item xs={12}>
      <ListContextProvider value={context}>
        <div className={classes.scrollTable}>
          <ListView open noResultsText="resources.arenaslots.messages.no_slots_added" rowsPerPageOptions={false}>
            <Datagrid>
              <TimeRangeField startSource="startTime" endSource="endTime" />
              <TextField source="duration" />
              <SurfaceField source="surfaceId" cellClassName={classes.truncate} />
              <TextField source="name" cellClassName={classes.truncate} />
              <div className={classes.actionButtons}>
                <ShowButton />
                <RemoveButton slots={slots} />
              </div>
            </Datagrid>
          </ListView>
        </div>
      </ListContextProvider>
    </Grid>
  </Grid>
}

const AddSlotButton = () => {
  const { change, batch, getState, blur, focus, pauseValidation, resumeValidation } = useForm();
  const { pristine } = useFormState();
  const translate = useTranslate();
  const notify = useNotify();
  const seasonId = useSeason();
  const surfaces = useSelector(state => state.admin.resources.surfaces.data);

  const handleAddSlot = useCallback(() => {
    resumeValidation();

    const { values, hasValidationErrors } = getState();

    if (hasValidationErrors) {
      blur('officeId');
      blur('eventType');
      blur('startTime');
      blur('endTime');
      blur('surfaceId');
      blur('name');
      return;
    }

    const { eventType, startTime, endTime, duration, surfaceId, name, leagueIds, categoryIds, teamIds, types, comments, isShared, slots: currentSlots } = values;

    const timezone = surfaces[surfaceId]?.timezone || 'UTC';

    const newSlot = {
      id: uniqueId('slot-'),
      eventType,
      name,
      startTime: moment(startTime).tz(timezone, true).format('YYYY-MM-DDTHH:mm:ssZ'),
      endTime: moment(endTime).tz(timezone, true).format('YYYY-MM-DDTHH:mm:ssZ'),
      timezone,
      duration: formatDuration({ duration, startTime, endTime }),
      surfaceId,
      leagueIds,
      categoryIds,
      teamIds,
      types,
      comments,
      isShared,
      seasonId
    };

    if (isOverlap(currentSlots, newSlot)) {
      notify('resources.arenaslots.validations.overlaps', 'warning');
      return;
    }


    const newSlots = sortBy([...(currentSlots || []), newSlot], ['startTime']);

    batch(() => {
      change('slots', newSlots);
      change('startTime', endTime);

      const updatedEndTime = moment(endTime).add(duration || moment(endTime).diff(startTime, 'minutes'), 'minutes').toISOString();
      change('endTime', updatedEndTime);
    });

    pauseValidation(false);
    focus('startTime');
  }, [blur, change, batch, getState, notify, resumeValidation, pauseValidation, seasonId, surfaces, focus]);

  useKeyboardEvent('Enter', () => {
    handleAddSlot();
  });

  return <Button variant="contained" color="primary" fullWidth startIcon={<Add />} onClick={handleAddSlot} disabled={pristine}>
    {translate('resources.arenaslots.actions.add_slot')}
  </Button>
}

const RestrictionsFields = () => {
  const [open, setOpen] = useState(false);
  const translate = useTranslate();
  const classes = useStyles();

  return <>
    <Grid item xs={12}>
      <div className={classes.collapseLabel}>
        <Typography variant="subtitle2" color="textSecondary" component="h6" style={{ fontWeight: 500, marginTop: 8 }}>
          {translate('resources.arenaslots.labels.restrictions')}
        </Typography>
        <IconButton size="small" onClick={() => setOpen(!open)}>
          {open ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
        </IconButton>
      </div>
    </Grid>
    <Collapse in={open} style={{ paddingInline: 8 }}>
      <Grid item xs={12}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <CategoryInput source="categoryIds" multiple helperText="resources.arenaslots.helpers.categoryIds" {...inputProps} />
          </Grid>
          <Grid item xs={12}>
            <ArenaSlotTypeEnumInput source="types" multiple helperText="resources.arenaslots.helpers.types" {...inputProps} />
          </Grid>
          <Grid item xs={12}>
            <OfficeInput source="leagueIds" multiple helperText="resources.arenaslots.helpers.leagueIds" filter={{ type: ['League', 'Tournament', 'Cup'] }} {...inputProps} />
          </Grid>
        </Grid>
      </Grid>
    </Collapse>
  </>
}

const TemplateFields = () => {
  const translate = useTranslate();
  const form = useForm();
  const { surfaces } = useAssociationSchedulingContext();

  const initialValues = form.getState().initialValues;

  const hasInitialSurface = !!initialValues?.surfaceId;
  const hasInitialOffice = !!initialValues?.officeId;

  const surfaceInputFilter = surfaces?.length ? { id: { inq: surfaces.map(({ id }) => id) } } : {};

  return <Grid container spacing={2}>
    <Grid item xs={12}>
      <OfficeInput source="officeId" disabled={hasInitialOffice} {...inputProps} />
    </Grid>
    <Grid item xs={12}>
      <SurfaceInput source="surfaceId" disabled={hasInitialSurface} filter={surfaceInputFilter} {...inputProps} />
    </Grid>
    <Grid item xs={12}>
      <EventTypeEnumInput source="eventType" helperText={false} {...inputProps} />
    </Grid>
    <Grid item xs={4}>
      <DurationInput source="duration" min={0} step={5} helperText="ra.message.optional" {...inputProps} />
    </Grid>
    <Grid item xs={4}>
      <TimeInput
        source="startTime"
        label={translate('resources.arenaslots.fields.startTime')}
        defaultTimezone={defaultTimezone}
        {...inputProps}
      />
    </Grid>
    <Grid item xs={4}>
      <EndTimeInput
        source="endTime"
        label={translate('resources.arenaslots.fields.endTime')}
        defaultTimezone={defaultTimezone}
        {...inputProps}
      />
    </Grid>

    <FieldDependency fieldSource="eventType" test={value => value === SURFACE_EVENT_TYPES.PRACTICE}>
      <Grid item xs={12}>
        <TeamInput source="teamIds" label="resources.practices.fields.teamIds" multiple helperText="ra.message.optional" {...inputProps} />
      </Grid>
    </FieldDependency>

    <Grid item xs={12}>
      <AddSlotButton />
    </Grid>

    <Grid item xs={12}>
      <Typography variant="subtitle2" color="textSecondary" component="h6" style={{ fontWeight: 500, marginTop: 8 }}>
        {translate('resources.arenaslots.labels.general')}
      </Typography>
    </Grid>
    <Grid item xs={12}>
      <AutoGeneratedNameInput source="name" {...inputProps} />
    </Grid>
    <Grid item xs={12}>
      <TextInput source="comments" multiline minRows="2" helperText="ra.message.optional" {...inputProps} />
    </Grid>
    <FieldDependency fieldSource="eventType" test={value => value === SURFACE_EVENT_TYPES.TIMESLOT}>
      <Grid item xs={12}>
        <SwitchInput source="isShared" helperText={false} {...inputProps} />
      </Grid>
      <RestrictionsFields />
    </FieldDependency>
  </Grid>
}

function TemplateStep() {
  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6} lg={5}>
        <Card variant="outlined" style={{ padding: 16 }}>
          <TemplateFields />
        </Card>
      </Grid>
      <Grid item xs={12} md={6} lg={7}>
        <Card variant="outlined">
          <SlotsGrid />
        </Card>
      </Grid>
    </Grid>
  )
}

export default TemplateStep
